export const PHONE_MASK_7 = '+7 (9##) ###-##-##';
export const PHONE_MASK_8 = '8 (9##) ###-##-##';
export const PHONE_FOCUS_PREFIX = '+7 (9';
const PHONE_PREFIX_8 = '8 (9';

/**
 * Накладывает маску на цифры номера.
 * @param {String} mask - шаблон, где # — цифра
 * @param {String} digits - только цифры без кода страны
 * @return {String}
 */
export function applyPhoneMask(mask, digits) {
    let result = '';
    let index = 0;

    for (let i = 0; i < mask.length; i++) {
        const char = mask.charAt(i);

        if (char === '#') {
            if (index >= digits.length) {
                break;
            }

            result += digits.charAt(index);
            index += 1;
        } else if (index < digits.length || index === 0) {
            result += char;
        } else {
            break;
        }
    }

    return result;
}

/**
 * Оставляет только цифры.
 * @param {String} value
 * @return {String}
 */
function onlyDigits(value) {
    return String(value || '').replace(/\D/g, '');
}

/**
 * Префикс, который нельзя стереть: +7 (9 или 8 (9.
 * @param {String} prevDigits
 * @return {String}
 */
function stickyPrefix(prevDigits) {
    return String(prevDigits || '').charAt(0) === '8' ? PHONE_PREFIX_8 : PHONE_FOCUS_PREFIX;
}

/**
 * Полный мобильный номер: 79XXXXXXXXX, 89XXXXXXXXX или 9XXXXXXXXX.
 * Если стёрли только код 8 у уже набранного 89..., код сохраняется.
 * @param {String} digits
 * @param {String} [prevDigits]
 * @return {String|null}
 */
function toExactMobile(digits, prevDigits = '') {
    if (/^[78]9\d{9}$/.test(digits)) {
        return digits;
    }

    if (/^9\d{9}$/.test(digits)) {
        if (prevDigits.charAt(0) === '8' && prevDigits.slice(1) === digits) {
            return `8${digits}`;
        }

        return `7${digits}`;
    }

    return null;
}

/**
 * Последний полный номер внутри строки цифр.
 * Нужен, когда перед номером есть лишние цифры (00, дубль кода).
 * @param {String} digits
 * @return {String|null}
 */
function extractLastMobile(digits) {
    let found = null;

    for (let i = 0; i <= digits.length - 11; i++) {
        const chunk = digits.slice(i, i + 11);

        if (/^[78]9\d{9}$/.test(chunk)) {
            found = chunk;
        }
    }

    if (found) {
        return found;
    }

    for (let i = 0; i <= digits.length - 10; i++) {
        const chunk = digits.slice(i, i + 10);

        if (/^9\d{9}$/.test(chunk)) {
            found = `7${chunk}`;
        }
    }

    return found;
}

/**
 * Ищет номер, вставленный целиком: в хвосте после текущего префикса
 * или внутри строки с лишними цифрами.
 * Посимвольный ввод сюда не попадает.
 * @param {String} digits
 * @param {String} prevDigits
 * @return {String|null}
 */
function isSingleDigitEdit(digits, prevDigits) {
    if (!prevDigits || Math.abs(digits.length - prevDigits.length) !== 1) {
        return false;
    }

    const shorter = digits.length < prevDigits.length ? digits : prevDigits;
    const longer = digits.length < prevDigits.length ? prevDigits : digits;
    let index = 0;

    while (index < shorter.length && shorter.charAt(index) === longer.charAt(index)) {
        index += 1;
    }

    return longer.slice(index + 1) === shorter.slice(index);
}

function extractInsertedPhone(digits, prevDigits) {
    const extendsPrev = Boolean(prevDigits) && digits.startsWith(prevDigits);
    const inserted = extendsPrev ? digits.length - prevDigits.length : digits.length;

    if (inserted <= 1 || isSingleDigitEdit(digits, prevDigits)) {
        return null;
    }

    if (extendsPrev) {
        return findMobile(digits.slice(prevDigits.length));
    }

    if (digits.length > 11) {
        return findMobile(digits);
    }

    return null;
}

/**
 * Целый номер или номер, спрятанный за лишними цифрами.
 * Короткий хвост — это дописанные цифры, а не новая вставка.
 * @param {String} digits
 * @return {String|null}
 */
function findMobile(digits) {
    const exact = toExactMobile(digits);

    if (exact) {
        return exact;
    }

    if (digits.length <= 11) {
        return null;
    }

    return extractLastMobile(digits);
}

/**
 * Цифры, набранные перед липким префиксом (+7/8 9), переносим в хвост номера.
 * Иначе 3+|79 → 379 → ломается в 79379.
 * @param {String} digits
 * @param {String} prevDigits
 * @return {String}
 */
function movePrependedDigitsToTail(digits, prevDigits) {
    if (
        prevDigits.length < 2 ||
        !/^[78]9/.test(prevDigits) ||
        digits.length <= prevDigits.length ||
        !digits.endsWith(prevDigits)
    ) {
        return digits;
    }

    if (/^[78]9\d{9}$/.test(prevDigits)) {
        return prevDigits;
    }

    return prevDigits + digits.slice(0, digits.length - prevDigits.length);
}

/**
 * Оставляет код 7/8 и обязательную 9, остальное — не больше 9 цифр.
 * @param {String} digits
 * @param {String} prevDigits
 * @return {String}
 */
function enforceMobilePrefix(digits, prevDigits) {
    let normalized = movePrependedDigitsToTail(digits, prevDigits);

    if (prevDigits.charAt(0) === '7' && normalized.charAt(0) === '8' && normalized.slice(1) === prevDigits) {
        normalized = `8${prevDigits.slice(1)}`;
    } else if (prevDigits.charAt(0) === '8' && normalized.charAt(0) === '7' && normalized.slice(1) === prevDigits) {
        normalized = `7${prevDigits.slice(1)}`;
    }

    let country = '7';
    let subscriber = normalized;

    if (normalized.charAt(0) === '8') {
        country = '8';
        subscriber = normalized.slice(1);
    } else if (normalized.charAt(0) === '7') {
        country = '7';
        subscriber = normalized.slice(1);
    } else if (prevDigits.charAt(0) === '8') {
        country = '8';
    }

    if (subscriber.charAt(0) !== '9') {
        subscriber = `9${subscriber}`;
    }

    return country + subscriber.slice(0, 10);
}

/**
 * @param {String} digits
 * @param {String} prevDigits
 * @return {String}
 */
function resolvePhoneDigits(digits, prevDigits) {
    const exact = toExactMobile(digits, prevDigits);

    if (exact) {
        return exact;
    }

    const pasted = extractInsertedPhone(digits, prevDigits);

    if (pasted) {
        return pasted;
    }

    return enforceMobilePrefix(digits, prevDigits);
}

/**
 * @param {String} digits - 79... или 89...
 * @return {String}
 */
function formatResolved(digits) {
    const subscriberRest = digits.slice(2);

    if (digits.charAt(0) === '8') {
        return applyPhoneMask(PHONE_MASK_8, subscriberRest);
    }

    return applyPhoneMask(PHONE_MASK_7, subscriberRest);
}

/**
 * Форматирует ввод телефона на лету.
 * Номер всегда начинается с +79 или 89, этот префикс нельзя стереть.
 * 79991234567 → +7 (999) 123-45-67
 * 89991234567 → 8 (999) 123-45-67
 * 9991234567  → +7 (999) 123-45-67
 * @param {String} value - текущее значение инпута
 * @param {Object} [options]
 * @param {Boolean} [options.isDeletion] - удаление: пустое поле возвращает префикс, а не пустую строку
 * @param {String} [options.previousValue] - прошлое значение, чтобы вставка и автозаполнение не склеивались с префиксом
 * @return {String}
 */
export function formatPhone(value, options = {}) {
    const digits = onlyDigits(value);
    const prevDigits = onlyDigits(options.previousValue);

    if (!digits) {
        if (options.isDeletion) {
            return stickyPrefix(prevDigits);
        }

        return '';
    }

    return formatResolved(resolvePhoneDigits(digits, prevDigits));
}

/**
 * Проверяет, заполнен ли номер по маске целиком.
 * @param {String} value
 * @return {Boolean}
 */
export function isPhoneComplete(value) {
    return /^[78]9\d{9}$/.test(onlyDigits(value));
}

/**
 * Префикс +7 (9 для пустого поля при фокусе.
 * @param {String} value
 * @return {String|null} новое значение или null, если менять не нужно
 */
export function getPhoneValueOnFocus(value) {
    if (onlyDigits(value).length === 0) {
        return PHONE_FOCUS_PREFIX;
    }

    return null;
}

/**
 * Находит native input внутри STInput или обычного input.
 * @param {Vue|HTMLElement} componentOrEl
 * @return {HTMLInputElement|null}
 */
export function getPhoneInputEl(componentOrEl) {
    if (!componentOrEl) {
        return null;
    }

    const root = componentOrEl.$el || componentOrEl;

    if (root instanceof HTMLInputElement) {
        return root;
    }

    return root.querySelector?.('input') || null;
}

/**
 * Ставит каретку в конец инпута.
 * @param {HTMLInputElement} input
 */
export function movePhoneCaretToEnd(input) {
    if (!input?.setSelectionRange) {
        return;
    }

    const {length} = input.value;

    input.setSelectionRange(length, length);
}

/**
 * Синхронизирует DOM-значение с отформатированным номером.
 * @param {HTMLInputElement} input
 * @param {String} formatted
 * @param {String} previousValue
 */
export function syncPhoneInput(input, formatted, previousValue) {
    if (!input) {
        return;
    }

    if (input.value !== formatted) {
        input.value = formatted;
    }

    if (formatted !== previousValue) {
        movePhoneCaretToEnd(input);
    }
}

/**
 * Подставляет +7 (9 в пустое поле и ставит каретку в конец.
 * @param {Vue} vm
 * @param {String} fieldName
 * @param {String} refName
 */
export function handlePhoneFocus(vm, fieldName, refName) {
    const nextValue = getPhoneValueOnFocus(vm[fieldName]);

    if (nextValue === null) {
        return;
    }

    vm[fieldName] = nextValue;
    vm.$nextTick(() => {
        movePhoneCaretToEnd(getPhoneInputEl(vm.$refs[refName]));
    });
}

/**
 * Форматирует ввод и синхронизирует инпут.
 * @param {Vue} vm
 * @param {String} fieldName
 * @param {String} refName
 * @param {String} value
 */
export function handlePhoneInput(vm, fieldName, refName, value) {
    const previous = String(vm[fieldName] || '');
    const next = String(value || '');
    const formatted = formatPhone(next, {
        isDeletion   : next.length < previous.length,
        previousValue: previous
    });

    vm[fieldName] = formatted;
    vm.$nextTick(() => {
        syncPhoneInput(getPhoneInputEl(vm.$refs[refName]), formatted, next);
    });
}
